/**
 * Platform Admin session · SEPARATE storage key from the client
 * session (sh.session) so a service-provider admin and a client
 * account can coexist in the same browser without clobbering.
 */

export interface AdminSession {
  token: string;
  expiresAt: string;
  user: { id?: string; email: string; name: string; role: string };
}

const KEY = "sh.adminSession";

export function getAdminSession(): AdminSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as AdminSession;
    if (s.expiresAt && new Date(s.expiresAt).getTime() < Date.now()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveAdminSession(s: AdminSession) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clearAdminSession() {
  localStorage.removeItem(KEY);
}

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * Fetch helper for the Platform Admin console. Uses the ADMIN token
 * (sh.adminSession) — deliberately not the shared client api helper,
 * which sends the client token from sh.session.
 */
export async function adminFetch<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = getAdminSession()?.token;
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = undefined;
  }
  if (!res.ok) {
    const msg =
      (parsed as { error?: { message?: string } })?.error?.message ??
      `${method} ${path} → ${res.status}`;
    throw new Error(msg);
  }
  return parsed as T;
}
